<patron-modal
  [visible]="visible"
  title="Manutenção em Massa"
  size="xl"
  confirmButtonText="Aplicar as modificações"
  confirmButtonIcon="bi-check2"
  [confirmButtonLoading]="saving"
  [confirmButtonDisabled]="!hasAnyChange || !bwUsers.length || !!pendingMainField"
  cancelButtonText="Cancelar"
  (confirm)="onApply()"
  (cancel)="onCancel()"
  [scrollable]="true">
  <div class="row bulk-maint">
    <!-- Coluna esquerda: quem vai ser alterado -->
    <div class="col-lg-4 mb-3">
      <div class="card h-100">
        <div class="card-header flex align-items-center justify-content-between">
          <span>Usuários selecionados</span>
          <patron-badge color="info">{{ bwUsers.length }} usuário(s)</patron-badge>
        </div>
        <div class="card-body p-0 bulk-maint-list">
          <ul patron-list-group [flush]="true">
            @for (bwUser of bwUsers; track bwUser.id) {
              <li patron-list-group-item [class.bulk-maint-item--failed]="!!failureOf(bwUser)">
                <div class="flex align-items-start justify-content-between gap-2">
                  <div>
                    <div>
                      <strong>{{ bwUser.name || bwUser.email }}</strong>
                    </div>
                    <div>
                      <small class="text-muted">{{ bwUser.email }}</small>
                    </div>
                    <div class="mt-1">
                      @if (bwUser.is_user) {
                        <patron-badge [color]="patronUtils.getAccessTypeColor('user')" class="me-1">
                          {{ patronUtils.getAccessTypeLabel('user') }}
                        </patron-badge>
                      }
                      @if (bwUser.is_customer) {
                        <patron-badge
                          [color]="patronUtils.getAccessTypeColor('customer')"
                          class="me-1">
                          {{ patronUtils.getAccessTypeLabel('customer') }}
                        </patron-badge>
                      }
                      @if (bwUser.is_supplier) {
                        <patron-badge
                          [color]="patronUtils.getAccessTypeColor('supplier')"
                          class="me-1">
                          {{ patronUtils.getAccessTypeLabel('supplier') }}
                        </patron-badge>
                      }
                    </div>
                    @if (failureOf(bwUser)) {
                      <div class="mt-1">
                        <small class="text-danger">{{ failureOf(bwUser) }}</small>
                      </div>
                    }
                  </div>
                  <patron-button
                    size="sm"
                    color="secondary"
                    variant="ghost"
                    iconName="bi-x"
                    title="Remover da seleção"
                    (click)="removeBwUser(bwUser)"></patron-button>
                </div>
              </li>
            }
          </ul>
        </div>
      </div>
    </div>

    <!-- Coluna direita: o que alterar -->
    <div class="col-lg-8">
      <patron-alert color="info">
        Marque a caixa do campo que deseja alterar. Somente os campos marcados serão replicados nos
        usuários selecionados, e nada é gravado antes de você clicar em
        <strong>Aplicar as modificações</strong>.
      </patron-alert>

      @if (bwUsers.length > largeBatchThreshold) {
        <patron-alert color="warning">
          Seleção grande ({{ bwUsers.length }} usuários) — pode levar alguns instantes.
        </patron-alert>
      }

      @if (erpDisabledReason) {
        <patron-alert color="warning">{{ erpDisabledReason }}</patron-alert>
      }

      @if (pendingMainField) {
        <patron-alert color="warning">
          Escolha o registro de <strong>{{ pendingMainField }}</strong> ou desmarque o campo —
          marcar sem escolher não teria efeito nenhum.
        </patron-alert>
      }

      <patron-form [formGroup]="form">
        @for (group of visibleGroups; track group.key) {
          <div class="card mb-3">
            <div class="card-header bulk-maint-group-header" (click)="toggleGroup(group.key)">
              <span>
                <patron-icon
                  [name]="openGroups[group.key] ? 'bi-chevron-down' : 'bi-chevron-right'" />
                {{ group.title }}
              </span>
              @if (countEnabled(group.fields)) {
                <patron-badge color="warning">{{ countEnabled(group.fields) }}</patron-badge>
              }
            </div>
            @if (openGroups[group.key]) {
              @switch (group.key) {
                @case ('access') {
                  <div class="card-body">
                    <!-- Perfis -->
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('bw_user_profiles')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('bw_user_profiles')"
                          [disabled]="!isAvailable('bw_user_profiles')"
                          (change)="toggleField('bw_user_profiles', $any($event.target).checked)" />
                        <span>Perfis</span>
                      </label>
                      @if (profilesDisabledReason) {
                        <small class="bulk-maint-help text-danger">{{
                          profilesDisabledReason
                        }}</small>
                      } @else {
                        <div class="row">
                          <div class="col-md-5">
                            <patron-select
                              [formGroup]="form"
                              formControlName="bw_user_profiles_mode"
                              label="Modo"
                              bindValue="value"
                              bindLabel="label"
                              [items]="relationModes"
                              [searchable]="false"
                              [isDisabled]="!isEnabled('bw_user_profiles')"></patron-select>
                          </div>
                          <div class="col-md-7">
                            <patron-select
                              [multiple]="true"
                              [formGroup]="form"
                              formControlName="bw_user_profiles"
                              label="Perfis"
                              bindLabel="name"
                              bindValue="id"
                              modelName="profiles"
                              [findOptions]="profilesFindOptions"
                              [isDisabled]="!isEnabled('bw_user_profiles')"></patron-select>
                          </div>
                        </div>
                        <small class="bulk-maint-help">
                          Só são oferecidos perfis compatíveis com todos os selecionados.
                        </small>
                      }
                    </div>

                    <!-- Ativo do grupo empresa -->
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('active')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('active')"
                          (change)="toggleField('active', $any($event.target).checked)" />
                        <span>Acesso a este grupo empresa</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="active"
                        label="Acesso a este grupo empresa"
                        [disabled]="!isEnabled('active')"></patron-switch>
                      <small class="bulk-maint-help">
                        Não é a coluna "Ativo" da listagem — são campos diferentes.
                      </small>
                    </div>

                    <!-- Mostrar preços -->
                    @if (isAvailable('show_prices')) {
                      <div
                        class="bulk-maint-field"
                        [class.bulk-maint-field--on]="isEnabled('show_prices')">
                        <label class="bulk-maint-check">
                          <input
                            type="checkbox"
                            [checked]="isEnabled('show_prices')"
                            (change)="toggleField('show_prices', $any($event.target).checked)" />
                          <span>Mostrar preços</span>
                        </label>
                        <patron-switch
                          [formGroup]="form"
                          formControlName="show_prices"
                          label="Mostrar preços"
                          [trueValue]="1"
                          [falseValue]="0"
                          [disabled]="!isEnabled('show_prices')"></patron-switch>
                        <small class="bulk-maint-help"> Só se aplica a acessos de cliente. </small>
                      </div>
                    }
                  </div>
                }
                @case ('specific') {
                  <div class="card-body">
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('read_all_customers')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('read_all_customers')"
                          [disabled]="!isAvailable('read_all_customers')"
                          (change)="
                            toggleField('read_all_customers', $any($event.target).checked)
                          " />
                        <span>Pode ver todos os clientes</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="read_all_customers"
                        label="Pode ver todos os clientes"
                        [trueValue]="1"
                        [falseValue]="0"
                        [disabled]="!isEnabled('read_all_customers')"></patron-switch>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('emit_nfe')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('emit_nfe')"
                          [disabled]="!isAvailable('emit_nfe')"
                          (change)="toggleField('emit_nfe', $any($event.target).checked)" />
                        <span>Pode gerar nota fiscal</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="emit_nfe"
                        label="Pode gerar nota fiscal"
                        [trueValue]="1"
                        [falseValue]="0"
                        [disabled]="!isEnabled('emit_nfe')"></patron-switch>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('order_counterpassword')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('order_counterpassword')"
                          [disabled]="!isAvailable('order_counterpassword')"
                          (change)="
                            toggleField('order_counterpassword', $any($event.target).checked)
                          " />
                        <span>Pede contra senha no pedido</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="order_counterpassword"
                        label="Pede contra senha no pedido"
                        [trueValue]="1"
                        [falseValue]="0"
                        [disabled]="!isEnabled('order_counterpassword')"></patron-switch>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('itemFilter')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('itemFilter')"
                          [disabled]="!isAvailable('itemFilter')"
                          (change)="toggleField('itemFilter', $any($event.target).checked)" />
                        <span>Filtro de itens</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="itemFilter"
                        label="Filtro de itens"
                        [disabled]="!isEnabled('itemFilter')"></patron-switch>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('itemExclude')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('itemExclude')"
                          [disabled]="!isAvailable('itemExclude') || !isEnabled('itemFilter')"
                          (change)="toggleField('itemExclude', $any($event.target).checked)" />
                        <span>Utilizar filtro de itens para bloqueio</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="itemExclude"
                        label="Utilizar filtro de itens para bloqueio"
                        [disabled]="!isEnabled('itemExclude')"></patron-switch>
                      <small class="bulk-maint-help">
                        Só é aplicável junto com o filtro de itens ligado — com o filtro desligado o
                        bloqueio é sempre desligado.
                      </small>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('discountLimit')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('discountLimit')"
                          [disabled]="!isAvailable('discountLimit')"
                          (change)="toggleField('discountLimit', $any($event.target).checked)" />
                        <span>Regras de limite para descontos</span>
                      </label>
                      <small class="bulk-maint-help">
                        As regras abaixo substituem por completo as regras atuais de cada usuário.
                        Marcar sem nenhuma regra remove todas.
                      </small>
                      @if (isEnabled('discountLimit')) {
                        @for (rule of discountLimits; track $index; let i = $index) {
                          <div class="row align-items-center mb-2">
                            <div class="col-4">
                              <div class="input-group input-group-sm">
                                <div class="input-group-prepend" style="width: 70px">
                                  <span class="input-group-text">De R$</span>
                                </div>
                                <input
                                  currencyMask
                                  class="form-control form-control-sm"
                                  [(ngModel)]="rule.minValue"
                                  [ngModelOptions]="{ standalone: true }"
                                  [options]="{
                                    prefix: '',
                                    thousands: '.',
                                    decimal: ',',
                                    align: 'left',
                                  }" />
                              </div>
                            </div>
                            <div class="col-4">
                              <div class="input-group input-group-sm">
                                <div class="input-group-prepend" style="width: 70px">
                                  <span class="input-group-text">Até R$</span>
                                </div>
                                <input
                                  currencyMask
                                  class="form-control form-control-sm"
                                  [(ngModel)]="rule.maxValue"
                                  [ngModelOptions]="{ standalone: true }"
                                  [options]="{
                                    prefix: '',
                                    thousands: '.',
                                    decimal: ',',
                                    align: 'left',
                                  }" />
                              </div>
                            </div>
                            <div class="col-4 flex align-items-center">
                              <div class="input-group input-group-sm mr-2">
                                <div class="input-group-prepend" style="width: 60px">
                                  <span class="input-group-text">Máx</span>
                                </div>
                                <input
                                  type="number"
                                  class="form-control form-control-sm"
                                  [(ngModel)]="rule.discount"
                                  [ngModelOptions]="{ standalone: true }" />
                                <div class="input-group-append">
                                  <span class="input-group-text">%</span>
                                </div>
                              </div>
                              <patron-button
                                size="sm"
                                color="danger"
                                iconName="bi-trash3"
                                title="Remover regra"
                                (click)="removeDiscountLimit(i)"></patron-button>
                            </div>
                          </div>
                        }
                        <patron-button
                          size="sm"
                          variant="outline"
                          iconName="bi-plus"
                          (click)="addDiscountLimit()">
                          Adicionar regra
                        </patron-button>
                      }
                    </div>
                  </div>
                }
                @case ('licenses') {
                  <div class="card-body">
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('licenses_web')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('licenses_web')"
                          [disabled]="!isAvailable('licenses_web')"
                          (change)="toggleField('licenses_web', $any($event.target).checked)" />
                        <span>Licenças — Web</span>
                      </label>
                      <patron-form-field
                        [formGroup]="form"
                        formControlName="licenses_web"
                        type="number"
                        label="Quantidade"
                        [isDisabled]="!isEnabled('licenses_web')"></patron-form-field>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('licenses_mobile')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('licenses_mobile')"
                          [disabled]="!isAvailable('licenses_mobile')"
                          (change)="toggleField('licenses_mobile', $any($event.target).checked)" />
                        <span>Licenças — Mobile</span>
                      </label>
                      <patron-form-field
                        [formGroup]="form"
                        formControlName="licenses_mobile"
                        type="number"
                        label="Quantidade"
                        [isDisabled]="!isEnabled('licenses_mobile')"></patron-form-field>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('daily_sales_goals')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('daily_sales_goals')"
                          [disabled]="!isAvailable('daily_sales_goals')"
                          (change)="
                            toggleField('daily_sales_goals', $any($event.target).checked)
                          " />
                        <span>Meta diária</span>
                      </label>
                      <patron-form-field
                        [formGroup]="form"
                        formControlName="daily_sales_goals"
                        type="currency"
                        label="Valor"
                        [isDisabled]="!isEnabled('daily_sales_goals')"></patron-form-field>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('monthly_sales_goals')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('monthly_sales_goals')"
                          [disabled]="!isAvailable('monthly_sales_goals')"
                          (change)="
                            toggleField('monthly_sales_goals', $any($event.target).checked)
                          " />
                        <span>Meta Mensal</span>
                      </label>
                      <patron-form-field
                        [formGroup]="form"
                        formControlName="monthly_sales_goals"
                        type="currency"
                        label="Valor"
                        [isDisabled]="!isEnabled('monthly_sales_goals')"></patron-form-field>
                    </div>
                  </div>
                }
                @case ('templates') {
                  <div class="card-body">
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('reports_charts')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('reports_charts')"
                          [disabled]="!isAvailable('reports_charts')"
                          (change)="toggleField('reports_charts', $any($event.target).checked)" />
                        <span>Tipo de gráfico</span>
                      </label>
                      <div class="row">
                        <div class="col-md-5">
                          <patron-select
                            [formGroup]="form"
                            formControlName="reports_charts_mode"
                            label="Modo"
                            bindValue="value"
                            bindLabel="label"
                            [items]="relationModes"
                            [searchable]="false"
                            [isDisabled]="!isEnabled('reports_charts')"></patron-select>
                        </div>
                        <div class="col-md-7">
                          <patron-select
                            [multiple]="true"
                            [formGroup]="form"
                            formControlName="reports_charts"
                            label="Tipo de gráfico"
                            bindLabel="title"
                            bindValue="id"
                            modelName="reports_charts"
                            [isDisabled]="!isEnabled('reports_charts')"></patron-select>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                @case ('relations') {
                  <div class="card-body">
                    <!-- Vendedor: único no domínio, sem modo -->
                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('seller_id')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('seller_id')"
                          [disabled]="!isAvailable('seller_id')"
                          (change)="toggleField('seller_id', $any($event.target).checked)" />
                        <span>Vendedor vinculado</span>
                      </label>
                      <patron-select
                        [formGroup]="form"
                        formControlName="seller_id"
                        label="Vendedor vinculado"
                        bindLabel="_name"
                        bindValue="id"
                        modelName="sellers"
                        [isDisabled]="!isEnabled('seller_id')"></patron-select>
                      <small class="bulk-maint-help">
                        O usuário tem um único vendedor: o vínculo atual é substituído. Deixe em
                        branco para desvincular.
                      </small>
                    </div>

                    @for (relation of relationFields; track relation.field) {
                      <div
                        class="bulk-maint-field"
                        [class.bulk-maint-field--on]="isEnabled(relation.field)">
                        <label class="bulk-maint-check">
                          <input
                            type="checkbox"
                            [checked]="isEnabled(relation.field)"
                            [disabled]="!isAvailable(relation.field)"
                            (change)="toggleField(relation.field, $any($event.target).checked)" />
                          <span>{{ relation.label }}</span>
                        </label>
                        <div class="row">
                          <div class="col-md-5">
                            <patron-select
                              [formGroup]="form"
                              [formControlName]="relation.field + '_mode'"
                              label="Modo"
                              bindValue="value"
                              bindLabel="label"
                              [items]="relationModes"
                              [searchable]="false"
                              [isDisabled]="!isEnabled(relation.field)"></patron-select>
                          </div>
                          <div class="col-md-7">
                            <patron-select
                              [multiple]="true"
                              [formGroup]="form"
                              [formControlName]="relation.field"
                              [label]="relation.label"
                              [bindLabel]="relation.bindLabel"
                              bindValue="id"
                              [modelName]="relation.modelName"
                              [findOptions]="findOptionsFor(relation)"
                              [isDisabled]="!isEnabled(relation.field)"></patron-select>
                          </div>
                        </div>
                        @if (relation.help) {
                          <small class="bulk-maint-help">{{ relation.help }}</small>
                        }
                      </div>
                    }

                    <patron-alert color="info">
                      <strong>Clientes</strong> vem do vendedor — use o campo Vendedor acima.
                      <strong>Operações</strong> é somente leitura.
                    </patron-alert>
                  </div>
                }
                @case ('pivots') {
                  <div class="card-body">
                    @for (pivot of mainPivotFields; track pivot.field) {
                      <div
                        class="bulk-maint-field"
                        [class.bulk-maint-field--on]="isEnabled(pivot.field)">
                        <label class="bulk-maint-check">
                          <input
                            type="checkbox"
                            [checked]="isEnabled(pivot.field)"
                            [disabled]="!isAvailable(pivot.field)"
                            (change)="toggleField(pivot.field, $any($event.target).checked)" />
                          <span>{{ pivot.label }}</span>
                        </label>
                        <patron-select
                          [formGroup]="form"
                          [formControlName]="pivot.field"
                          [label]="pivot.label"
                          [bindLabel]="pivot.bindLabel"
                          bindValue="id"
                          [modelName]="pivot.modelName"
                          [isDisabled]="!isEnabled(pivot.field)"></patron-select>
                        <small class="bulk-maint-help">
                          O registro escolhido passa a ser o padrão; o padrão anterior é desmarcado.
                          Precisa já estar vinculado ao usuário.
                        </small>
                      </div>
                    }

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('counting_stock_locale_ids')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('counting_stock_locale_ids')"
                          [disabled]="!isAvailable('counting_stock_locale_ids')"
                          (change)="
                            toggleField('counting_stock_locale_ids', $any($event.target).checked)
                          " />
                        <span>Contagem</span>
                      </label>
                      <patron-select
                        [multiple]="true"
                        [formGroup]="form"
                        formControlName="counting_stock_locale_ids"
                        label="Contagem"
                        bindLabel="name"
                        bindValue="id"
                        modelName="stock-locales"
                        [isDisabled]="!isEnabled('counting_stock_locale_ids')"></patron-select>
                      <small class="bulk-maint-help">
                        <strong>Atenção:</strong> define a lista completa. Os locais escolhidos
                        passam a ser de contagem e <strong>todos os demais deixam de ser</strong> —
                        marcar sem escolher nenhum remove a contagem de todos os locais dos usuários
                        selecionados.
                      </small>
                    </div>

                    <div
                      class="bulk-maint-field"
                      [class.bulk-maint-field--on]="isEnabled('department_manager')">
                      <label class="bulk-maint-check">
                        <input
                          type="checkbox"
                          [checked]="isEnabled('department_manager')"
                          [disabled]="!isAvailable('department_manager')"
                          (change)="
                            toggleField('department_manager', $any($event.target).checked)
                          " />
                        <span>Gerente</span>
                      </label>
                      <patron-switch
                        [formGroup]="form"
                        formControlName="department_manager"
                        label="Gerente"
                        [disabled]="!isEnabled('department_manager')"></patron-switch>
                      <small class="bulk-maint-help">
                        Aplicado aos departamentos informados no grupo Vínculos quando o modo é
                        adicionar ou substituir; nos demais casos, a todos os departamentos do
                        usuário.
                      </small>
                    </div>
                  </div>
                }
              }
            }
          </div>
        }
      </patron-form>
    </div>
  </div>
</patron-modal>
